import { Temporal } from "@js-temporal/polyfill";
import { isString } from "es-toolkit";
import moment from "moment-timezone";
import { keepNonNullish } from "./array.mjs";
import { microsoftSupportedTimeZones } from "./date-microsoft.mjs";
import { isNil } from "./null.mjs";
const defaultTimeZone = "Etc/UTC";
const supportedTimeZones = moment.tz.names().filter((timeZone) => {
  const isSupportedByMicrosoft = microsoftSupportedTimeZones.includes(timeZone);
  const isAbbreviated = /^[A-Z]{3,4}\d[A-Z]{3,4}$/.test(timeZone);
  const isEtcGMT = timeZone.startsWith("Etc/GMT");
  return isSupportedByMicrosoft && !isAbbreviated && !isEtcGMT;
});
const parseTimeZone = (inputTimeZone) => {
  const normalized = inputTimeZone.trim().toLowerCase();
  for (const timeZone of supportedTimeZones) {
    if (timeZone.toLowerCase() !== normalized) continue;
    return timeZone;
  }
  if (normalized === "utc") return parseTimeZone("Etc/UTC");
};
const guessTimeZone = () => {
  const timeZone = moment.tz.guess();
  return parseTimeZone(timeZone) ?? defaultTimeZone;
};
const isValidTimeZone = (inputTimeZone) => {
  const timeZone = parseTimeZone(inputTimeZone);
  return !isNil(timeZone) && inputTimeZone === timeZone;
};
const isDateISO8601 = (date) => {
  return moment(date, moment.ISO_8601, true).isValid();
};
const isDateISO8601WithOffset = (date) => {
  return isDateISO8601(date) && (moment.parseZone(date).utcOffset() !== 0 || date.endsWith("+00:00"));
};
const toDateISO8601WithOffset = (date, timezoneOrOffset) => {
  const result = isNil(timezoneOrOffset) ? date : isString(timezoneOrOffset) ? moment(date).tz(timezoneOrOffset).toISOString(true) : moment(date).utcOffset(timezoneOrOffset).toISOString(true);
  return result.replace(/\.\d{3}/, "");
};
const isDateContainsTimezone = (date) => {
  return moment.parseZone(date).utcOffset() !== 0 || date.endsWith("Z") || date.endsWith("+00:00");
};
const stripDateTimezone = (date) => {
  const offset = moment.parseZone(date).utcOffset();
  const result = moment.utc(date).add(offset, "minutes").toISOString();
  return result.endsWith("Z") ? result.slice(0, -1) : result;
};
const appendDateTimezone = (date, timezone) => {
  const baseDate = isDateContainsTimezone(date) ? stripDateTimezone(date) : date;
  const convertedDate = moment.tz(baseDate, timezone).format();
  return convertedDate;
};
const getTimezoneInfo = (timezone, epochMilliseconds) => {
  const instant = Temporal.Instant.fromEpochMilliseconds(epochMilliseconds);
  const zdt = instant.toZonedDateTimeISO(timezone);
  const offsetMinutes = Math.round(zdt.offsetNanoseconds / 6e10);
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");
  const offset = `${sign}${hours}:${minutes}`;
  const nextTransition = zdt.getTimeZoneTransition("next");
  if (!nextTransition) return { offset, dstState: void 0 };
  const willSpringForward = nextTransition.offsetNanoseconds > zdt.offsetNanoseconds;
  const dstState = willSpringForward ? "standard time" : "daylight time";
  return { offset, dstState };
};
const parseTimestampString = (input) => {
  const firstBracketIndex = input.indexOf("[");
  const datetime = firstBracketIndex === -1 ? input : input.substring(0, firstBracketIndex);
  const offsetMatch = datetime.match(/[+-]\d{2}:\d{2}$/);
  const offset = offsetMatch?.[0];
  const localDatetime = offset ? datetime.slice(0, -offset.length) : datetime;
  let timezone;
  let dayOfWeek;
  let dstState;
  const bracketRegex = /\[([^\]]{1,256})\]/g;
  let match;
  while ((match = bracketRegex.exec(input)) !== null) {
    const content = match[1];
    if (!content) break;
    const parsedTz = parseTimeZone(content);
    if (!isNil(parsedTz)) {
      timezone = parsedTz;
      continue;
    }
    if (["standard time", "daylight time"].includes(content)) {
      dstState = content;
      continue;
    }
    if (moment(content, "dddd", true).isValid()) {
      dayOfWeek = content;
      continue;
    }
  }
  return { datetime, localDatetime, annotations: { offset, timezone, dayOfWeek, dstState } };
};
const toZonedTimestamp = (input, overrideTimezone) => {
  let date;
  if (moment.isMoment(input)) {
    date = input;
  } else if (isString(input)) {
    const parsed = parseTimestampString(input);
    date = moment.parseZone(parsed.datetime, moment.ISO_8601, true);
    if (parsed.annotations.timezone) {
      date = date.tz(parsed.annotations.timezone);
    }
  } else {
    date = moment(input);
  }
  const zonedDate = overrideTimezone ? date.tz(overrideTimezone) : date;
  const timezone = zonedDate.tz();
  const timezoneInfo = timezone ? getTimezoneInfo(timezone, zonedDate.valueOf()) : void 0;
  const datetime = zonedDate.format("YYYY-MM-DDTHH:mm:ssZ");
  const dayOfWeek = zonedDate.format("dddd");
  const annotations = [timezone, dayOfWeek, timezoneInfo?.dstState].filter(keepNonNullish).map((annotation) => `[${annotation}]`);
  const result = `${datetime}${annotations.join("")}`;
  return result;
};
const toShortZonedTimestamp = (input, timezone) => {
  const zonedDate = moment(input).tz(timezone);
  return `${zonedDate.format("YYYY-MM-DDTHH:mm:ssZ")}[${zonedDate.format("dddd")}]`;
};
const fromZonedTimestamp = (zonedTimestamp) => {
  const { datetime, localDatetime, annotations } = parseTimestampString(zonedTimestamp);
  if (!annotations.offset) {
    throw new Error("Zoned timestamp must include an explicit numeric UTC offset (Z is not accepted)");
  }
  if (!annotations.timezone) {
    throw new Error("Zoned timestamp must include an IANA timezone in brackets");
  }
  const parsed = moment(datetime, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    throw new Error("Invalid timestamp format");
  }
  const { offset: expectedOffset } = getTimezoneInfo(annotations.timezone, parsed.valueOf());
  if (annotations.offset !== expectedOffset) {
    throw new Error(
      `Offset ${annotations.offset} does not match timezone ${annotations.timezone} for ${localDatetime}. Expected ${expectedOffset}`
    );
  }
  return parsed.tz(annotations.timezone);
};
const fromZonedTimestampSafe = (input) => {
  try {
    return { success: true, value: fromZonedTimestamp(input) };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Invalid zoned timestamp" };
  }
};
const getWeekdayNumber = (input) => {
  if (typeof input === "string") {
    const parsedAsWeekday = moment(input, "dddd", true);
    const weekday2 = parsedAsWeekday.isoWeekday();
    if (Number.isFinite(weekday2)) return weekday2;
  }
  const parsed = moment(input);
  const weekday = parsed.isoWeekday();
  if (Number.isFinite(weekday)) return weekday;
  return void 0;
};
const getWeekdayName = (input) => {
  if (typeof input === "number" && (!Number.isFinite(input) || input < 1 || input > 7)) {
    return void 0;
  }
  const date = typeof input === "number" ? moment().isoWeekday(input) : moment(input);
  if (!date.isValid()) return void 0;
  return date.format("dddd");
};
export {
  appendDateTimezone,
  defaultTimeZone,
  fromZonedTimestamp,
  fromZonedTimestampSafe,
  getWeekdayName,
  getWeekdayNumber,
  guessTimeZone,
  isDateContainsTimezone,
  isDateISO8601,
  isDateISO8601WithOffset,
  isValidTimeZone,
  parseTimeZone,
  stripDateTimezone,
  supportedTimeZones,
  toDateISO8601WithOffset,
  toShortZonedTimestamp,
  toZonedTimestamp
};
//# sourceMappingURL=date.mjs.map
